import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import type { ComponentProps, HTMLAttributes, ReactNode } from "react";

import { cn } from "#/lib/utils";

type SheetSide = "left" | "right" | "top" | "bottom";
type SheetSize = "sm" | "md" | "lg" | "xl";

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: SheetSide;
  size?: SheetSize;
  children: ReactNode;
}

const sizeMap: Record<SheetSize, string> = {
  sm: "w-80",
  md: "w-96",
  lg: "w-[480px]",
  xl: "w-[600px]",
};

const sideMap: Record<SheetSide, string> = {
  right: "right-0 top-0 bottom-0 h-full",
  left: "left-0 top-0 bottom-0 h-full",
  top: "top-0 left-0 right-0 w-full h-auto max-h-[80vh]",
  bottom: "bottom-0 left-0 right-0 w-full h-auto max-h-[80vh]",
};

const slideMap: Record<SheetSide, string> = {
  right: "data-open:slide-in-from-right data-closed:slide-out-to-right",
  left: "data-open:slide-in-from-left data-closed:slide-out-to-left",
  top: "data-open:slide-in-from-top data-closed:slide-out-to-top",
  bottom: "data-open:slide-in-from-bottom data-closed:slide-out-to-bottom",
};

function Sheet({
  open,
  onOpenChange,
  side = "right",
  size = "md",
  children,
}: SheetProps) {
  const widthCls = side === "left" || side === "right" ? sizeMap[size] : "";
  return (
    <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[100] bg-foreground/40",
            "data-open:animate-in data-closed:animate-out",
            "data-open:fade-in-0 data-closed:fade-out-0",
            "duration-[120ms]",
          )}
        />
        <SheetPrimitive.Popup
          data-slot="sheet-content"
          data-side={side}
          className={cn(
            "fixed z-[100] bg-card shadow-2xl flex flex-col max-w-full outline-none",
            sideMap[side],
            widthCls,
            "data-open:animate-in data-closed:animate-out",
            slideMap[side],
            "data-open:duration-150 data-closed:duration-[120ms] ease-out",
            "will-change-transform",
          )}
        >
          {children}
        </SheetPrimitive.Popup>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  );
}

function SheetHeader({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "flex items-start justify-between p-6 border-b border-border",
        className,
      )}
    >
      <div className="flex flex-col gap-1">{children}</div>
      <SheetPrimitive.Close
        aria-label="Close"
        className="h-9 w-9 rounded-full bg-muted hover:bg-border flex items-center justify-center cursor-pointer text-foreground border-0 shrink-0"
      >
        <X className="size-4" />
      </SheetPrimitive.Close>
    </div>
  );
}

function SheetTitle({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("font-bold text-xl text-foreground m-0", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm text-muted-foreground m-0", className)}
      {...props}
    />
  );
}

function SheetBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="sheet-body"
      className={cn("flex-1 overflow-auto p-6", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("p-6 border-t border-border bg-muted/30", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetBody,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  type SheetProps,
  type SheetSide,
  type SheetSize,
  SheetTitle,
};
